import { Button, Icon, Input } from '@yes.technology/react-toolkit'
import { useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useJsApiLoader } from '@react-google-maps/api'

import { useCalcUserAddressFromLatLong } from '../../../hooks/useCalcUserAddressFromLatLong'
import useUpdateGeolocation from './useUpdateGeolocation'
import useAddressFromModel from './useAddressFromModel'
import GeolocationModal from 'modules/shared/GeolocationModal/GeolocationModal'
import useGeocodeCallback from 'modules/shared/hooks/useGeocodeCallback'
import { useAppSelector } from 'state/store'
import { GeolocationDependencies } from 'types/shared'

export type ActivityInputGeolocationProps = {
  id: string
  label: string
  required?: boolean
  value: string
  dependencies: GeolocationDependencies
  parentPath?: string[]
  fillGeolocationValues: boolean

  onChange: (value: string) => void
}

export default function ActivityInputGeolocation({
  id,
  label,
  required,
  value = '',
  dependencies,
  onChange,
  parentPath,
  fillGeolocationValues
}: ActivityInputGeolocationProps) {
  const [isModalOpen, toggleModal] = useReducer((state) => !state, false)
  const disableGeolocationProcessing = useRef(false)
  const { t } = useTranslation()

  const { googleMapsApiKey = '' } = window
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey })

  const interactionModel = useAppSelector(
    ({ interactions: { interactionModel } }) => interactionModel
  )
  const activitiesByUuid = interactionModel.flatData?.activity_values || {}

  const address = useAddressFromModel({
    activitiesByUuid,
    dependencies,
    parentPath
  })

  const coord = interactionModel?.geolocation

  const { processUserAddressFromLatLong } = useCalcUserAddressFromLatLong(
    dependencies,
    coord
  )

  const geocodeCallback = useGeocodeCallback()
  useUpdateGeolocation({ address, geocodeCallback, onChange, value })

  if (
    fillGeolocationValues &&
    !disableGeolocationProcessing.current &&
    isLoaded &&
    !!coord
  ) {
    disableGeolocationProcessing.current = true

    processUserAddressFromLatLong()
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'flex-end',
        gap: '4px'
      }}
    >
      <Input
        heightClass='regular'
        informational={true}
        onChange={onChange}
        value={value}
        id={id}
        required={required}
        label={label}
      />
      <Button
        variant='secondary'
        onClick={toggleModal}
        aria-label={t('activity-input-geolocation.labels.set')}
        disabled={!value}
      >
        <Icon iconName='Pin' />
      </Button>
      <GeolocationModal
        open={isModalOpen}
        handleClose={toggleModal}
        {...{ id, value, onChange }}
      />
    </div>
  )
}
